import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import InviteInternalUser from "~/components/Project/inviteInternalUser";
import PermitDescriptionTooltip from "~/components/Project/PermitDescriptionTooltip";
import DisplayInvitedMembers from "~/components/User/displayInvitedMembers";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProjectApi } from "~/functions/api/project";
import close from "~/images/close.svg";
import type { InviteProjectBody, InviteUser, ProjectRole } from "~/interface/project";
import type { User } from "~/interface/user";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

// UI表示用に日本語化されたロール名を含む型
interface AddedMemberDisplayInfo {
  id: string;
  displayName: string;
  role: string; // 日本語化されたロール
}

interface InvitableMemberItem {
  user: User;
  role: ProjectRole;
  isChecked: boolean;
}

interface ModalProps {
  isAdmin: boolean;
  isOpen: boolean;
  onClose: () => void;
  onMembersAdded?: (addedMembers: AddedMemberDisplayInfo[]) => void;
}

// モーダル内の一時的なロールマッピング (将来的には共通化を検討)
const ROLE_MAP_MODAL: { [key: string]: string } = {
  owner: "オーナー",
  contributor: "編集者",
  member: "閲覧者",
};

const mapRoleToJapanese_MODAL = (role: string): string => {
  return ROLE_MAP_MODAL[role.toLowerCase()] || role;
};

const InviteProjectModal: React.FC<ModalProps> = ({ isOpen, onClose, onMembersAdded }) => {
  const [invitableMembers, setInvitableMembers] = useState<InvitableMemberItem[]>([]);
  const [isLock, setIsLock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const currentProject = useSelector((state: RootState) => state.auth.currentProject);

  // キャッシュ関連のref
  const cachedDataRef = useRef<{
    data: InvitableMemberItem[] | null;
    timestamp: number;
    projectId: string;
    organizationId: string;
  }>({
    data: null,
    timestamp: 0,
    projectId: "",
    organizationId: "",
  });

  // キャッシュの有効期限（5分）
  const CACHE_DURATION = 5 * 60 * 1000;

  const getCheckedUsers = (data: InvitableMemberItem[]) => {
    return data
      .filter((item) => item.isChecked)
      .map(
        (item): InviteUser => ({
          user_id: item.user.id,
          role: item.role as ProjectRole,
        }),
      );
  };

  const handleGetInvitableUsers = async () => {
    const projectApi = new ProjectApi();
    const response = await projectApi.getInvitableUser(
      currentUser.organization_id,
      currentProject.id,
    );

    if (response !== null) {
      const users = response;
      return users;
    } else {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.FETCH_INVITABLE_USERS_FAILED);
      return null;
    }
  };

  // キャッシュが有効かどうかをチェック
  const isCacheValid = () => {
    const now = Date.now();
    const cache = cachedDataRef.current;

    return (
      cache.data &&
      cache.projectId === currentProject.id &&
      cache.organizationId === currentUser.organization_id &&
      now - cache.timestamp < CACHE_DURATION
    );
  };

  const handleSubmit = async () => {
    setIsLock(true);

    if (!currentProject?.id) {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.PROJECT_NOT_SELECTED);
      setIsLock(false);
      return;
    }

    if (!currentUser?.id) {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.USER_NOT_FOUND);
      setIsLock(false);
      return;
    }

    const selectedUsersToInviteAPI = getCheckedUsers(invitableMembers);

    if (selectedUsersToInviteAPI.length === 0) {
      toast.info(TOAST_MESSAGES.PROJECT_MEMBER.SELECT_MEMBER);
      setIsLock(false);
      return;
    }

    // UI更新用に、日本語ロール名を含むメンバー情報を作成
    const selectedMembersForUI: AddedMemberDisplayInfo[] = invitableMembers
      .filter((item) => item.isChecked)
      .map((item) => ({
        id: item.user.id,
        displayName: item.user.display_name,
        role: mapRoleToJapanese_MODAL(item.role), // API送信前のロール名から日本語へ
      }));

    const inviteInternalUserData: InviteProjectBody = {
      users: selectedUsersToInviteAPI,
    };

    const projectApi = new ProjectApi();

    const response = await projectApi.invite(
      currentUser.organization_id,
      currentProject.id,
      inviteInternalUserData,
    );

    if (response !== null) {
      // API成功判定は既存のまま
      toast.success(TOAST_MESSAGES.PROJECT_MEMBER.ADD_MEMBER_SUCCESS);
      if (onMembersAdded) {
        onMembersAdded(selectedMembersForUI); // UI更新用の情報を渡す
      }

      // メンバー追加後はキャッシュを無効化（次回モーダル開時に最新データを取得）
      cachedDataRef.current = {
        data: null,
        timestamp: 0,
        projectId: "",
        organizationId: "",
      };

      // setTimeout(() => navigate(0), 300); // ページリロードを削除
      onClose(); // モーダルを閉じる
    } else {
      toast.error(TOAST_MESSAGES.PROJECT_MEMBER.ADD_MEMBER_FAILED);
      console.error("メンバーの追加に失敗しました: "); // response の内容次第で詳細なエラーログ
    }
    setIsLock(false);
  };

  useEffect(() => {
    const fetchInvitableUsers = async () => {
      // モーダルが開いていない場合は何もしない
      if (!isOpen) return;

      // 必要なデータが揃っていない場合は何もしない
      if (!currentProject?.id || !currentUser?.organization_id) {
        return;
      }

      // キャッシュが有効な場合はAPIを呼ばずにキャッシュデータを使用
      if (isCacheValid()) {
        setInvitableMembers(cachedDataRef.current.data!);
        return;
      }

      setIsLoading(true);

      try {
        const response = await handleGetInvitableUsers();
        if (response) {
          const invitableUsers = response;
          const memberItems = invitableUsers.map((user) => ({
            user: user,
            role:
              user.role === "guest" ? ("member" as ProjectRole) : ("contributor" as ProjectRole),
            isChecked: false,
          }));

          // データをキャッシュに保存
          cachedDataRef.current = {
            data: memberItems,
            timestamp: Date.now(),
            projectId: currentProject.id,
            organizationId: currentUser.organization_id,
          };

          setInvitableMembers(memberItems);
        } else {
          setInvitableMembers([]);
        }
      } catch (_error) {
        toast.error(TOAST_MESSAGES.PROJECT_MEMBER.FETCH_INVITABLE_USERS_ERROR);
        setInvitableMembers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitableUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    currentProject?.id,
    currentUser?.organization_id,
    handleGetInvitableUsers,
    isCacheValid,
  ]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    setInvitableMembers((prevMembers) =>
      prevMembers.map((member) => ({ ...member, isChecked: false })),
    );
    onClose();
  };

  return (
    <div className={styles["overlay"]}>
      <div className={styles["modal"]}>
        <div className={styles["modalHeader"]}>
          <h2>メンバー追加</h2>
          <button className={styles["closeButton"]} onClick={handleClose}>
            <img src={close} alt="閉じる" />
          </button>
        </div>
        <div className={styles["modalContent"]}>
          <p className={styles["description"]}>
            組織に参加しているメンバーを招待できます。選択時には必ず権限を選択してください。
          </p>
          <PermitDescriptionTooltip>
            <div className={styles["infoSection"]}>
              <button className={styles["infoButton"]}>?</button>
              <span className={styles["infoText"]}>権限について</span>
            </div>
          </PermitDescriptionTooltip>
          <div className={styles["inviteInternalMembers"]}>
            <span className={styles["itemName"]}>組織内のメンバーを追加</span>
            {isLoading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <span>招待可能なユーザーを読み込み中...</span>
              </div>
            ) : (
              <>
                <DisplayInvitedMembers members={invitableMembers} />
                <InviteInternalUser members={invitableMembers} setMembers={setInvitableMembers} />
              </>
            )}
          </div>
        </div>
        <div className={styles["modalFooter"]}>
          <Button label="キャンセル" styleType="white" onClick={handleClose} />
          <Button
            label={!isLock ? "追加" : "追加中"}
            styleType="blue"
            onClick={handleSubmit}
            disabled={isLock}
            dataTestId="invite-submit"
          />
        </div>
      </div>
    </div>
  );
};

export default InviteProjectModal;
